// apps/web/src/features/cargo/forms/schemas.ts
//
// Form-layer Zod schemas + value↔wire mapping for the Cargo add/edit modals
// (#1232). The shared `Create/UpdateCargoArticleInput` schemas keep numeric
// columns as nullable "dirty text"; the FORM models them as plain `string`
// fields with numeric-like validation (comma OR dot, optional spaces), then
// `toCreateInput`/`toUpdatePatch` normalize them to the source-compatible wire
// strings. We do NOT reuse the shared schemas as the resolver because they
// permit arbitrary dirty text — the UX requires numeric validation in the form,
// EXCEPT the two tariff columns, which are free text (#2141): China logists
// enter currency mixes / tariff combinations there, not plain numbers.

import { z } from 'zod';
import {
  CargoCarrierPaymentStatusSchema,
  CargoStatusSchema,
  type CargoArticleWire,
  type CargoStatus,
  type CreateCargoArticleInput,
  type UpdateCargoArticleInput,
} from '@magimp/shared';

// WHY: a 3-value form-only enum that adds the derived "issued" state so the
// edit status selector can represent issuance directly. `issued` is NEVER stored
// on the backend — `toUpdatePatch` maps it to `arrived_rf` + a non-null release
// date. The ADD modal keeps `CargoStatusSchema` (2 stored values only).
export const CargoFormStatusSchema = z.enum(['in_transit', 'arrived_rf', 'issued']);

import {
  isValidCargoMoney,
  isValidCargoNumeric,
  normalizeCargoFreeTextInput,
  normalizeCargoMoneyInput,
  normalizeCargoNumericInput,
} from '../numeric.js';

// A free-text numeric-like form field. Blank is allowed (the column is
// nullable); a non-blank value must parse as a numeric-like token.
const numericText = z.string().refine(isValidCargoNumeric, { message: 'numeric' });

// A nonnegative money field (`chinaPaymentAmount`) bound to the strict wire
// money schema. Blank allowed; a non-blank value must be nonnegative with ≤2
// fraction digits.
const moneyText = z.string().refine(isValidCargoMoney, { message: 'money' });

// An optional date-only string (`YYYY-MM-DD`) from a native date input, or ''.
const dateText = z.string();

// A free-text field accepted verbatim (#2141). The two tariff columns store
// arbitrary text (currencies, combinations, formulas), so the form applies NO
// numeric validation — blank is allowed; any non-blank value passes.
const freeText = z.string();

export const CargoAddFormSchema = z.object({
  article: z.string().trim().min(1, { message: 'required' }),
  responsibleManagerId: z.string(),
  carrier: z.string(),
  pathFormat: z.string(),
  status: CargoStatusSchema,
  nameGoods: z.string(),
  numberOfSeats: numericText,
  weight: numericText,
  volume: numericText,
  transportationTariff: freeText,
  costGoods: numericText,
  insuranceCost: numericText,
  packagingCost: numericText,
  timeFromChina: dateText,
  totalCost: numericText,
});
export type CargoAddFormValues = z.infer<typeof CargoAddFormSchema>;

export const CargoEditFormSchema = z
  .object({
    article: z.string().trim().min(1, { message: 'required' }),
    responsibleManagerId: z.string(),
    carrier: z.string(),
    pathFormat: z.string(),
    status: CargoFormStatusSchema,
    nameGoods: z.string(),
    numberOfSeats: numericText,
    weight: numericText,
    volume: numericText,
    transportationTariff: freeText,
    transportationTariffForClients: freeText,
    costGoods: numericText,
    insuranceCost: numericText,
    packagingCost: numericText,
    totalCost: numericText,
    addressTransportationCost: numericText,
    timeFromChina: dateText,
    chinaPaymentAmount: moneyText,
    chinaWarehouseDispatchAmount: moneyText,
    chinaWarehouseInspectionAmount: moneyText,
    chinaWarehousePackagingAmount: moneyText,
    prr: numericText,
    tatCost: numericText,
    paymentToCarrierStatus: CargoCarrierPaymentStatusSchema,
    timeCargoArrivalToRf: dateText,
    timeCargoRelease: dateText,
  })
  // WHY: biconditional — "issued" <=> timeCargoRelease non-empty. Blocks saving
  // issued + no release date; the UI watch hook enforces the other directions.
  .refine((v) => v.status !== 'issued' || v.timeCargoRelease.trim().length > 0, {
    path: ['timeCargoRelease'],
    message: 'releaseDateRequired',
  });
export type CargoEditFormValues = z.infer<typeof CargoEditFormSchema>;

/** ISO date-only (`YYYY-MM-DD`) → start-of-day UTC ISO datetime, or null. */
function dateOnlyToIso(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  return `${trimmed}T00:00:00.000Z`;
}

/** ISO datetime → `YYYY-MM-DD` for a native date input, or '' when null. */
export function isoToDateOnly(iso: string | null): string {
  if (!iso) return '';
  return iso.slice(0, 10);
}

/** A nullable select value: '' (unset) → null, else the chosen string. */
function selectToNullable(value: string): string | null {
  return value.trim().length === 0 ? null : value;
}

export function emptyCargoAddValues(): CargoAddFormValues {
  return {
    article: '',
    responsibleManagerId: '',
    carrier: '',
    pathFormat: '',
    status: 'in_transit',
    nameGoods: '',
    numberOfSeats: '',
    weight: '',
    volume: '',
    transportationTariff: '',
    costGoods: '',
    insuranceCost: '',
    packagingCost: '',
    timeFromChina: '',
    totalCost: '',
  };
}

export function cargoRowToEditValues(row: CargoArticleWire): CargoEditFormValues {
  return {
    article: row.article,
    responsibleManagerId: row.responsibleManagerId ?? '',
    carrier: row.carrier ?? '',
    pathFormat: row.pathFormat ?? '',
    status: row.isIssued ? 'issued' : row.status,
    nameGoods: row.nameGoods ?? '',
    numberOfSeats: row.numberOfSeats ?? '',
    weight: row.weight ?? '',
    volume: row.volume ?? '',
    transportationTariff: row.transportationTariff ?? '',
    transportationTariffForClients: row.transportationTariffForClients ?? '',
    costGoods: row.costGoods ?? '',
    insuranceCost: row.insuranceCost ?? '',
    packagingCost: row.packagingCost ?? '',
    totalCost: row.totalCost ?? '',
    addressTransportationCost: row.addressTransportationCost ?? '',
    timeFromChina: isoToDateOnly(row.timeFromChina),
    chinaPaymentAmount: row.chinaPaymentAmount ?? '',
    chinaWarehouseDispatchAmount: row.chinaWarehouseDispatchAmount ?? '',
    chinaWarehouseInspectionAmount: row.chinaWarehouseInspectionAmount ?? '',
    chinaWarehousePackagingAmount: row.chinaWarehousePackagingAmount ?? '',
    prr: row.prr ?? '',
    tatCost: row.tatCost ?? '',
    paymentToCarrierStatus: row.paymentToCarrierStatus,
    timeCargoArrivalToRf: isoToDateOnly(row.timeCargoArrivalToRf),
    timeCargoRelease: isoToDateOnly(row.timeCargoRelease),
  };
}

/**
 * Map add-form values to the `POST /cargo` body. Numeric columns submit the
 * normalized source-compatible string (or null when blank). `responsibleManagerId`
 * is only sent when a manager was actually picked so the strict schema's
 * `uuid()` does not reject ''.
 */
export function toCreateInput(v: CargoAddFormValues): CreateCargoArticleInput {
  const input: CreateCargoArticleInput = {
    article: v.article.trim(),
    status: v.status,
    // The wire schema defaults these, but the inferred input type lists them as
    // required; the manual add never sets a paid status, so they stay default.
    paidByClientStatus: 'not_paid',
    paymentToCarrierStatus: 'not_paid',
    nameGoods: selectToNullable(v.nameGoods),
    numberOfSeats: normalizeCargoNumericInput(v.numberOfSeats),
    weight: normalizeCargoNumericInput(v.weight),
    volume: normalizeCargoNumericInput(v.volume),
    transportationTariff: normalizeCargoFreeTextInput(v.transportationTariff),
    costGoods: normalizeCargoNumericInput(v.costGoods),
    insuranceCost: normalizeCargoNumericInput(v.insuranceCost),
    packagingCost: normalizeCargoNumericInput(v.packagingCost),
    totalCost: normalizeCargoNumericInput(v.totalCost),
    timeFromChina: dateOnlyToIso(v.timeFromChina),
  };
  if (v.responsibleManagerId.trim().length > 0) {
    input.responsibleManagerId = v.responsibleManagerId;
  }
  if (v.carrier.trim().length > 0) input.carrier = v.carrier as CreateCargoArticleInput['carrier'];
  if (v.pathFormat.trim().length > 0) {
    input.pathFormat = v.pathFormat as CreateCargoArticleInput['pathFormat'];
  }
  return input;
}

/**
 * Map edit-form values to a `PATCH /cargo/:id` body. `chinaPaymentAmount` is
 * included only when `includeChinaPayment` (the actor may manage it) — the
 * backend rejects it otherwise. `responsibleManagerId` carries null when the
 * manager was cleared.
 *
 * WHY (#1947): `carrier` and `pathFormat` are emitted ONLY when the submitted
 * value differs from the row's originally-stored value. The edit dropdown
 * injects the current stored value so a user who touches no carrier field
 * re-submits the same string — omitting it avoids a backend 400 when the
 * stored value is a legacy name not in the active system list.
 */
export function toUpdatePatch(
  v: CargoEditFormValues,
  opts: {
    includeChinaPayment: boolean;
    includeCarrierPayment: boolean;
    originalCarrier: string | null;
    originalPathFormat: string | null;
  },
): UpdateCargoArticleInput {
  // WHY: `issued` is a form-only derived state — map it to the stored
  // `arrived_rf` + the release date. Non-issued statuses always clear the
  // release date (even if a stale date string is present in form values) so
  // the biconditional is enforced at the wire boundary too.
  let wireStatus: CargoStatus;
  let wireRelease: string | null;
  if (v.status === 'issued') {
    wireStatus = 'arrived_rf';
    wireRelease = dateOnlyToIso(v.timeCargoRelease);
  } else {
    wireStatus = v.status;
    wireRelease = null;
  }

  const patch: UpdateCargoArticleInput = {
    article: v.article.trim(),
    responsibleManagerId: selectToNullable(v.responsibleManagerId),
    status: wireStatus,
    nameGoods: selectToNullable(v.nameGoods),
    numberOfSeats: normalizeCargoNumericInput(v.numberOfSeats),
    weight: normalizeCargoNumericInput(v.weight),
    volume: normalizeCargoNumericInput(v.volume),
    transportationTariff: normalizeCargoFreeTextInput(v.transportationTariff),
    transportationTariffForClients: normalizeCargoFreeTextInput(v.transportationTariffForClients),
    costGoods: normalizeCargoNumericInput(v.costGoods),
    insuranceCost: normalizeCargoNumericInput(v.insuranceCost),
    packagingCost: normalizeCargoNumericInput(v.packagingCost),
    totalCost: normalizeCargoNumericInput(v.totalCost),
    addressTransportationCost: normalizeCargoNumericInput(v.addressTransportationCost),
    timeFromChina: dateOnlyToIso(v.timeFromChina),
    prr: normalizeCargoNumericInput(v.prr),
    tatCost: normalizeCargoNumericInput(v.tatCost),
    timeCargoArrivalToRf: dateOnlyToIso(v.timeCargoArrivalToRf),
    timeCargoRelease: wireRelease,
  };
  const nextCarrier = selectToNullable(v.carrier);
  if (nextCarrier !== opts.originalCarrier) {
    patch.carrier = nextCarrier as UpdateCargoArticleInput['carrier'];
  }
  const nextPathFormat = selectToNullable(v.pathFormat);
  if (nextPathFormat !== opts.originalPathFormat) {
    patch.pathFormat = nextPathFormat as UpdateCargoArticleInput['pathFormat'];
  }
  if (opts.includeChinaPayment) {
    const money = normalizeCargoMoneyInput(v.chinaPaymentAmount);
    // `undefined` only when the value failed numeric validation, which the
    // form blocks before submit; treat it as "leave unchanged" defensively.
    if (money !== undefined) patch.chinaPaymentAmount = money;
    const dispatch = normalizeCargoMoneyInput(v.chinaWarehouseDispatchAmount);
    if (dispatch !== undefined) patch.chinaWarehouseDispatchAmount = dispatch;
    const inspection = normalizeCargoMoneyInput(v.chinaWarehouseInspectionAmount);
    if (inspection !== undefined) patch.chinaWarehouseInspectionAmount = inspection;
    const packaging = normalizeCargoMoneyInput(v.chinaWarehousePackagingAmount);
    if (packaging !== undefined) patch.chinaWarehousePackagingAmount = packaging;
  }
  if (opts.includeCarrierPayment) {
    patch.paymentToCarrierStatus = v.paymentToCarrierStatus;
  }
  return patch;
}
